import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, TextField, Box, Typography } from '@mui/material';
import { Dayjs } from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers';
import {
  defaultEventCategorySet,
  RoutineActivity,
  RoutineActivityRecurring,
  RoutineCustomRecurrence,
  TaskItemDuration,
} from '@demind-inc/core';
import {
  AccessTime,
  ModeEdit,
  EditOutlined,
  LocalOfferOutlined,
  RepeatOutlined,
  ChevronRight,
  Close,
} from '@mui/icons-material';
import clsx from 'clsx';

import { defaultRecurring } from '../../AIScheduler';
import { useGeneralSettings } from '../../../../hooks';
import './RoutineInfoModal.scss';
import { validateRoutineActivity } from './validate';
import {
  isCustomRecurring,
  customWeeklyToLegacy,
  legacyToCustomWeekly,
} from '../recurrence.helpers';
import {
  FirestoreRoutineActivity,
  getNextFiveMinuteInterval,
  transformHHmmToDayjs,
  useAuthContext,
  usePreferences,
} from '../../../../data-access';
import { EventCategories } from '../../../CalendarView/EventMenu/EventCategories';
import { DurationField } from '../../../common';
import BasicDialog from '../../../common/BasicDialog/BasicDialog';
import { getCssVariable } from '../../../../utils';
import { RoutineRecurringSelector } from '../RoutineRecurringSelector';
import { CustomRecurrenceSelector } from '../CustomRecurrenceSelector';
import { formatWorkHoursRange } from '../../../../data-access';
import { transformDBPrefToWorkHourPref } from '../../AIScheduler/reducer/workHourPrefItem.state';
import { useTranslation } from 'react-i18next';

interface RoutineInfoModalProps {
  isOpen: boolean;
  selectedDate?: Dayjs;
  onClose: () => void;
  onSave: ({ id, activity }: { id?: string; activity: FirestoreRoutineActivity }) => void;
  onDelete: (activityId: string) => boolean | void | Promise<boolean | void>;
  mode: 'edit' | 'create';
  initialActivity?: RoutineActivity;
  isAlreadyScheduled?: boolean;
  defaultActivityName?: string;
}

const hasChanged = (
  activity: RoutineActivity | undefined,
  localActivity: FirestoreRoutineActivity
) => {
  if (!activity) {
    return true;
  }
  return (
    activity.name !== localActivity.name ||
    activity.startTime !== localActivity.startTime ||
    activity.endTime !== localActivity.endTime ||
    activity.durationMin !== localActivity.durationMin ||
    activity.category !== localActivity.category ||
    JSON.stringify(activity.recurring) !== JSON.stringify(localActivity.recurring)
  );
};

const colorTextBlack = getCssVariable('--color-black') || '#131314';
const colorGrayDark = getCssVariable('--color-gray-dark') || '#7c7c7c';
const ROUTINE_DURATION_MIN = 5;
const ROUTINE_DURATION_MAX = 120;

const lightTaskCategory =
  defaultEventCategorySet.find((category) => category.id === 'default_lightTask') ??
  defaultEventCategorySet[0];

export const RoutineInfoModal: React.FC<RoutineInfoModalProps> = ({
  isOpen,
  selectedDate,
  onClose,
  onSave,
  onDelete,
  mode,
  initialActivity,
  isAlreadyScheduled,
  defaultActivityName,
}) => {
  const { t } = useTranslation();
  const defaultActivity: FirestoreRoutineActivity = useMemo(
    () => ({
      name: defaultActivityName ?? '',
      startTime: null,
      endTime: null,
      durationMin: 30,
      recurring: defaultRecurring,
      category: lightTaskCategory,
    }),
    [defaultActivityName]
  );

  const [localActivity, setLocalActivity] = useState<FirestoreRoutineActivity>(defaultActivity);
  const [showErrors, setShowErrors] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showTimePreferences, setShowTimePreferences] = useState(
    Boolean(initialActivity?.startTime || initialActivity?.endTime)
  );

  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();

  const { user } = useAuthContext();
  const { schedulerPreferences } = usePreferences({ preferenceId: user?.preferenceId ?? '' });

  const workHoursLabel = useMemo(() => {
    const workHours = transformDBPrefToWorkHourPref(schedulerPreferences as any);
    return formatWorkHoursRange(
      workHours.start.time,
      workHours.end.time,
      timeFormat,
      workHours.endDayOffset
    );
  }, [schedulerPreferences, timeFormat]);

  // Update local state when props change
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (initialActivity) {
      setLocalActivity({
        ...initialActivity,
        category: initialActivity.category ?? lightTaskCategory,
      });
      setShowTimePreferences(Boolean(initialActivity?.startTime || initialActivity?.endTime));
    } else {
      setLocalActivity(defaultActivity);
      setShowTimePreferences(false);
    }
    setShowErrors(false);
  }, [defaultActivity, initialActivity, isOpen]);

  const { validationErrors, canSave } = useMemo(() => {
    const validationErrors = validateRoutineActivity(localActivity, {
      timePreferencesEnabled: showTimePreferences,
    });
    const canSave = validationErrors.length === 0 && hasChanged(initialActivity, localActivity);

    return { validationErrors, canSave };
  }, [localActivity, initialActivity, showTimePreferences]);

  const getValidationErrorText = (section: 'name' | 'time' | 'category') => {
    const error = validationErrors.find((validationError) => validationError.section === section);
    return error ? t(error.errorKey, { defaultValue: error.error }) : null;
  };

  const handleClose = () => {
    onClose();
    setLocalActivity(defaultActivity);
    setShowErrors(false);
    setDeleteConfirmOpen(false);
    setShowTimePreferences(false);
  };

  const ensureTimeDefaults = () => {
    const nextInterval = getNextFiveMinuteInterval();
    const fallbackStart = nextInterval.format('HH:mm');
    const fallbackEnd = nextInterval
      .add(Math.max(localActivity.durationMin || 30, 5), 'minute')
      .format('HH:mm');

    setLocalActivity((prev) => ({
      ...prev,
      startTime: prev.startTime ?? fallbackStart,
      endTime: prev.endTime ?? fallbackEnd,
    }));
  };

  const handleEnableTimePreferences = () => {
    setShowErrors(true);
    ensureTimeDefaults();
    setShowTimePreferences(true);
  };

  const handleDisableTimePreferences = () => {
    setShowErrors(true);
    setShowTimePreferences(false);
    setLocalActivity((prev) => ({
      ...prev,
      startTime: null,
      endTime: null,
    }));
  };

  const handleSave = () => {
    if (!canSave) {
      setShowErrors(true);
      return;
    }
    onSave({
      id: initialActivity?.routineActivityId,
      activity: {
        ...initialActivity,
        ...localActivity,
        category: localActivity.category ?? lightTaskCategory,
        ...(showTimePreferences ? {} : { startTime: null, endTime: null }),
      },
    });
    handleClose();
  };

  const handleStartTimeChange = (val: Dayjs | null) => {
    setShowErrors(true);

    if (val) {
      setLocalActivity({
        ...localActivity,
        startTime: val.format('HH:mm'),
      });
    }
  };

  const handleEndTimeChange = (val: Dayjs | null) => {
    setShowErrors(true);

    if (val) {
      setLocalActivity({
        ...localActivity,
        endTime: val.format('HH:mm'),
      });
    }
  };

  const handleDurationChange = (val: TaskItemDuration) => {
    setShowErrors(true);

    setLocalActivity({
      ...localActivity,
      durationMin: val.amount,
    });
  };

  const isCustom = isCustomRecurring(localActivity.recurring);

  const handleRecurrenceModeChange = (mode: 'weekly' | 'custom') => {
    setShowErrors(true);
    if (isCustomRecurring(localActivity.recurring)) {
      const recurring =
        mode === 'weekly' ? customWeeklyToLegacy(localActivity.recurring) : localActivity.recurring;
      setLocalActivity({ ...localActivity, recurring });
    } else {
      const recurring =
        mode === 'custom' ? legacyToCustomWeekly(localActivity.recurring) : localActivity.recurring;
      setLocalActivity({ ...localActivity, recurring });
    }
  };

  const handleDaySelect = (day: keyof RoutineActivityRecurring) => {
    if (isCustomRecurring(localActivity.recurring)) return;
    setShowErrors(true);
    const { recurring } = localActivity;
    setLocalActivity({
      ...localActivity,
      recurring: { ...recurring, [day]: { enabled: !recurring[day].enabled } },
    });
  };

  const handleCustomRecurrenceChange = (recurring: RoutineCustomRecurrence) => {
    setShowErrors(true);
    setLocalActivity({ ...localActivity, recurring });
  };

  const handleDelete = async () => {
    const shouldClose = await onDelete(initialActivity?.routineActivityId || '');
    if (shouldClose !== false) {
      handleClose();
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={handleClose} className="routine-info-modal">
        <div className="routine-info-modal__content">
          <div className="routine-info-modal__header">
            <div className="routine-info-modal__header-spacer" />
            <h3 className="routine-info-modal__title">
              {mode === 'create'
                ? t('routine.modal.addNewActivity')
                : t('routine.modal.editActivity')}
            </h3>
            <button
              type="button"
              className="routine-info-modal__close-button"
              onClick={handleClose}
              aria-label={t('data.common.close')}
            >
              <Close />
            </button>
          </div>

          <div className="routine-info-modal__body">
            {isAlreadyScheduled && (
              <div className="routine-info-modal__warning">
                <Typography className="routine-info-modal__warning-text" variant="caption">
                  {t('routine.modal.alreadyScheduled')}
                </Typography>
              </div>
            )}

            {/* Title */}
            <div className="routine-info-modal__section-block">
              <div className="routine-info-modal__section-label">{t('routine.modal.title')}</div>

              <div
                className={clsx('routine-info-modal__input-container', {
                  'routine-info-modal__input-container--disabled': isAlreadyScheduled,
                })}
              >
                <EditOutlined
                  className="routine-info-modal__inline-icon"
                  sx={{ color: colorTextBlack }}
                />
                <TextField
                  disabled={isAlreadyScheduled}
                  value={localActivity.name}
                  onChange={(e) => {
                    setShowErrors(true);
                    setLocalActivity({ ...localActivity, name: e.target.value });
                  }}
                  fullWidth
                  variant="outlined"
                  placeholder={t('routine.modal.titlePlaceholder')}
                  className="routine-info-modal__text-field"
                />
              </div>

              {showErrors && getValidationErrorText('name') && (
                <Typography color="error" variant="caption">
                  {getValidationErrorText('name')}
                </Typography>
              )}
            </div>

            {/* Time Preferences */}
            <div className="routine-info-modal__section-block">
              <div className="routine-info-modal__section-label">
                {t('routine.modal.timePreferences')}
              </div>

              <div className="routine-info-modal__time-preferences">
                {/* Duration */}
                <div
                  className={clsx('routine-info-modal__time-row', {
                    'routine-info-modal__time-row--disabled': isAlreadyScheduled,
                  })}
                >
                  <div className="routine-info-modal__time-row-label">
                    {t('routine.modal.duration')}:
                  </div>
                  <div className="routine-info-modal__pill">
                    <DurationField
                      value={{ amount: localActivity.durationMin || 0, unit: 'minute' }}
                      intervals={Array.from(
                        { length: (ROUTINE_DURATION_MAX - ROUTINE_DURATION_MIN) / 5 + 1 },
                        (_, i) => ROUTINE_DURATION_MIN + i * 5
                      )}
                      textStyle={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: getCssVariable('--color-primary') || '#680ddb',
                      }}
                      onChange={handleDurationChange}
                      disabled={isAlreadyScheduled}
                      showIcon={false}
                    />
                  </div>
                </div>

                {!showTimePreferences ? (
                  <button
                    type="button"
                    className={clsx('routine-info-modal__add-time-button', {
                      'routine-info-modal__add-time-button--disabled': isAlreadyScheduled,
                    })}
                    onClick={handleEnableTimePreferences}
                    disabled={isAlreadyScheduled}
                  >
                    <div className="routine-info-modal__add-time-content">
                      <div className="routine-info-modal__add-time-title-row">
                        <AccessTime className="routine-info-modal__add-time-icon" />
                        <div className="routine-info-modal__add-time-title">
                          {t('routine.modal.addTimeWindow')}
                        </div>
                      </div>
                      <div className="routine-info-modal__add-time-subtext">
                        {t('routine.modal.addTimeWindowDescription', { workHours: workHoursLabel })}
                      </div>
                    </div>
                    <ChevronRight className="routine-info-modal__add-time-chevron" />
                  </button>
                ) : (
                  <>
                    {/* Earliest start */}
                    <div
                      className={clsx('routine-info-modal__time-row', {
                        'routine-info-modal__time-row--disabled': isAlreadyScheduled,
                      })}
                    >
                      <div className="routine-info-modal__time-row-label">
                        {t('routine.modal.earliestStart')}:
                      </div>
                      <div className="routine-info-modal__pill">
                        <TimePicker
                          className="routine-info-modal__time-picker"
                          value={
                            localActivity.startTime
                              ? transformHHmmToDayjs(localActivity.startTime)
                              : getNextFiveMinuteInterval()
                          }
                          onChange={handleStartTimeChange}
                          ampm={timeFormat === '12h'}
                          format={timeFormat === '12h' ? 'h:mmA' : 'HH:mm'}
                          label=""
                          slotProps={{
                            popper: {
                              className: 'custom-time-picker-popper',
                            },
                          }}
                          slots={{
                            openPickerIcon: ModeEdit,
                          }}
                          disabled={isAlreadyScheduled}
                        />
                      </div>
                    </div>

                    {/* Latest end */}
                    <div
                      className={clsx('routine-info-modal__time-row', {
                        'routine-info-modal__time-row--disabled': isAlreadyScheduled,
                      })}
                    >
                      <div className="routine-info-modal__time-row-label">
                        {t('routine.modal.latestEnd')}:
                      </div>
                      <div className="routine-info-modal__pill">
                        <TimePicker
                          className="routine-info-modal__time-picker"
                          value={
                            localActivity.endTime
                              ? transformHHmmToDayjs(localActivity.endTime)
                              : localActivity.startTime
                                ? transformHHmmToDayjs(localActivity.startTime).add(
                                    Math.max(localActivity.durationMin || 30, 5),
                                    'minute'
                                  )
                                : getNextFiveMinuteInterval().add(
                                    Math.max(localActivity.durationMin || 30, 5),
                                    'minute'
                                  )
                          }
                          onChange={handleEndTimeChange}
                          ampm={timeFormat === '12h'}
                          format={timeFormat === '12h' ? 'h:mmA' : 'HH:mm'}
                          label=""
                          slotProps={{
                            popper: {
                              className: 'custom-time-picker-popper',
                            },
                          }}
                          slots={{
                            openPickerIcon: ModeEdit,
                          }}
                          disabled={isAlreadyScheduled}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className={clsx('routine-info-modal__remove-time-button', {
                        'routine-info-modal__remove-time-button--disabled': isAlreadyScheduled,
                      })}
                      onClick={handleDisableTimePreferences}
                      disabled={isAlreadyScheduled}
                    >
                      <Close className="routine-info-modal__remove-time-icon" />
                      <span className="routine-info-modal__remove-time-text">
                        {t('routine.modal.removeTimePreference')}
                      </span>
                    </button>
                  </>
                )}

                {showErrors && getValidationErrorText('time') && (
                  <Typography color="error" variant="caption">
                    {getValidationErrorText('time')}
                  </Typography>
                )}
              </div>
            </div>

            {/* Activity Type */}
            <div className="routine-info-modal__section-block">
              <div className="routine-info-modal__section-label">
                {t('routine.modal.activityType')}
              </div>

              <div className="routine-info-modal__input-container">
                <LocalOfferOutlined
                  className="routine-info-modal__inline-icon"
                  sx={{ color: colorTextBlack }}
                />
                <Box className="routine-info-modal__section-content">
                  <EventCategories
                    className="routine-info-modal__event-categories"
                    selectedCategory={localActivity.category ?? lightTaskCategory}
                    setSelectedCategory={(category) =>
                      setLocalActivity({ ...localActivity, category })
                    }
                  />
                </Box>
              </div>

              {showErrors && getValidationErrorText('category') && (
                <Typography color="error" variant="caption">
                  {getValidationErrorText('category')}
                </Typography>
              )}
            </div>

            {/* Recurring */}
            <div className="routine-info-modal__section-block">
              <div className="routine-info-modal__section-label">
                {t('routine.modal.recurring')}
              </div>
              <div className="routine-info-modal__recurring-container">
                <RepeatOutlined
                  className="routine-info-modal__planner-icon"
                  sx={{ color: colorTextBlack }}
                />
                <div className="routine-info-modal__recurring-content">
                  <div className="routine-info-modal__recurrence-mode-tabs">
                    <button
                      type="button"
                      className={clsx('routine-info-modal__recurrence-mode-tab', {
                        'routine-info-modal__recurrence-mode-tab--active': !isCustom,
                      })}
                      onClick={() => handleRecurrenceModeChange('weekly')}
                      disabled={isAlreadyScheduled}
                    >
                      {t('routine.modal.everyWeek')}
                    </button>
                    <button
                      type="button"
                      className={clsx('routine-info-modal__recurrence-mode-tab', {
                        'routine-info-modal__recurrence-mode-tab--active': isCustom,
                      })}
                      onClick={() => handleRecurrenceModeChange('custom')}
                      disabled={isAlreadyScheduled}
                    >
                      {t('routine.recurrence.custom')}
                    </button>
                  </div>
                  {isCustomRecurring(localActivity.recurring) ? (
                    <CustomRecurrenceSelector
                      value={localActivity.recurring}
                      onChange={handleCustomRecurrenceChange}
                      disabled={isAlreadyScheduled}
                    />
                  ) : (
                    <RoutineRecurringSelector
                      selectedDate={selectedDate}
                      recurring={localActivity.recurring}
                      onDaySelect={handleDaySelect}
                      className="routine-info-modal__recurring-selector"
                      disabled={isAlreadyScheduled}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="routine-info-modal__actions">
            {mode === 'edit' && (
              <Button
                variant="text"
                onClick={() => {
                  setDeleteConfirmOpen(true);
                }}
                className="routine-info-modal__delete-button"
              >
                {t('routine.modal.deleteRoutine')}
              </Button>
            )}
            <Button
              variant="text"
              onClick={handleClose}
              className="routine-info-modal__cancel-button"
            >
              {t('routine.modal.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              className={clsx('routine-info-modal__save-button', {
                'routine-info-modal__save-button--disabled': !canSave,
              })}
              disabled={!canSave}
            >
              {t('routine.modal.save')}
            </Button>
          </div>
        </div>
      </Modal>
      <BasicDialog
        visible={deleteConfirmOpen}
        title={t('routine.modal.deleteRoutine')}
        description={t('routine.modal.deleteRoutineDescription')}
        ctaString={t('routine.modal.delete')}
        onYes={() => {
          setDeleteConfirmOpen(false);
          void handleDelete();
        }}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};
